<template>
  <div class="library-page">
    <!-- Header -->
    <div class="library-header d-flex align-center justify-space-between px-3 px-sm-6 py-3">
      <div class="d-flex align-center" style="gap: 12px;">
        <v-btn icon @click="$router.push({ name: 'auth', params: { authPage: 'shop' } })">
          <v-icon>mdi-arrow-left</v-icon>
        </v-btn>
        <h2 class="mb-0 primary--text font-weight-bold" style="font-size: 20px;">
          <v-icon color="primary" class="mr-1" style="vertical-align: middle;">mdi-bookshelf</v-icon>
          Content Library
        </h2>
      </div>
      <!-- <span class="grey--text text--darken-1 d-none d-sm-inline" style="font-size: 13px;">
        Shared content from admin
      </span> -->
    </div>

    <v-divider />

    <!-- Body -->
    <div class="library-body pa-3 pa-sm-5">
      <v-card rounded="lg" class="pa-4" elevation="1">
        <!-- Toolbar: title + search + refresh -->
        <div class="d-flex align-center justify-space-between mb-4" style="gap: 12px; flex-wrap: wrap;">
          <h3 class="primary--text font-weight-bold mb-0 d-flex align-center" style="font-size: 15px;">
            <v-icon color="primary" small class="mr-1">mdi-image-multiple-outline</v-icon>
            Shared Content
            <v-chip v-if="library.total" x-small label color="primary" outlined class="ml-2 font-weight-bold">{{
              library.total
            }}</v-chip>
          </h3>

          <div class="d-flex align-center" style="gap: 8px; flex-wrap: wrap;">
            <v-text-field
              v-model="library.search"
              prepend-inner-icon="mdi-magnify"
              placeholder="Search content"
              hide-details
              dense
              outlined
              clearable
              class="library-search"
              @input="onLibrarySearch"
            />
            <v-btn
              depressed
              color="primary"
              :loading="library.isLoading"
              @click="
                library.page = 1;
                fetchLibrary();
              "
            >
              <v-icon small>mdi-refresh</v-icon>
            </v-btn>
          </div>
        </div>

        <!-- ===== Table + side detail panel ===== -->
        <v-row no-gutters class="library-row" style="gap: 0; align-items: flex-start;">
          <!-- Left: table -->
          <v-col
            cols="12"
            :md="detailPanel.show ? 8 : 12"
            class="library-table-col"
            :class="{ 'pr-md-4': detailPanel.show }"
          >
            <div class="table-scroll-wrapper">
              <v-data-table
                :headers="tableHeaders"
                :items="library.list"
                :loading="library.isLoading"
                :items-per-page="-1"
                hide-default-footer
                item-key="_id"
                class="report-table library-table"
              >
                <!-- Attachments -->
                <template v-slot:[`item.resource_input`]="{ item }">
                  <div v-if="getMediaList(item).length" class="d-flex flex-wrap py-1" style="gap: 5px;">
                    <a
                      v-for="(url, i) in getMediaList(item)"
                      :key="i"
                      :href="imageHelpers.url(url)"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <video
                        v-if="isVideo(url)"
                        :src="imageHelpers.url(url)"
                        width="36"
                        height="36"
                        class="attachment-thumb"
                        style="object-fit: cover; display: block;"
                      />
                      <v-img
                        v-else
                        :src="imageHelpers.url(url, { width: 60, height: 60 })"
                        width="36"
                        height="36"
                        class="rounded attachment-thumb"
                        contain
                      />
                    </a>
                  </div>
                  <span v-else class="grey--text">—</span>
                </template>

                <!-- Content -->
                <template v-slot:[`item.content`]="{ item }">
                  <div class="content-cell">
                    <v-tooltip top max-width="380">
                      <template v-slot:activator="{ on, attrs }">
                        <div v-bind="attrs" v-on="on" class="cell-clamp">
                          {{ getContent(item) || '—' }}
                        </div>
                      </template>
                      <span style="white-space: pre-wrap;">{{ getContent(item) || '—' }}</span>
                    </v-tooltip>
                    <v-tooltip top v-if="getContent(item)">
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn
                          v-bind="attrs"
                          v-on="on"
                          icon
                          x-small
                          class="copy-inline-btn"
                          @click.stop="copyContent(getContent(item))"
                        >
                          <v-icon size="14" :color="isCopied(getContent(item)) ? 'success' : 'grey darken-1'">
                            {{ isCopied(getContent(item)) ? 'mdi-check' : 'mdi-content-copy' }}
                          </v-icon>
                        </v-btn>
                      </template>
                      <span>{{ isCopied(getContent(item)) ? 'Copied!' : 'Copy content' }}</span>
                    </v-tooltip>
                  </div>
                </template>

                <!-- Created At -->
                <template v-slot:[`item.createdAt`]="{ item }">
                  <span class="created-cell grey--text text--darken-2">{{ formatDate(item.createdAt) }}</span>
                </template>

                <!-- Actions -->
                <template v-slot:[`item.actions`]="{ item }">
                  <div class="row-actions">
                    <v-btn
                      outlined
                      color="primary"
                      class="row-action-btn"
                      :class="{ 'row-action-btn--active': isActiveDetail(item) }"
                      @click="openDetail(item)"
                    >
                      <v-icon size="16" left>mdi-eye-outline</v-icon>
                      Detail
                    </v-btn>
                  </div>
                </template>

                <!-- Loading state -->
                <template v-slot:loading>
                  <div class="text-center py-6">
                    <v-progress-circular indeterminate color="primary" size="28" />
                  </div>
                </template>

                <!-- Empty state -->
                <template v-slot:no-data>
                  <div class="text-center py-8 grey--text">
                    <v-icon large color="grey lighten-2">mdi-bookshelf</v-icon>
                    <div class="mt-2" style="font-size: 13px;">
                      No content{{ library.search ? ' matching "' + library.search + '"' : '' }}
                    </div>
                  </div>
                </template>
              </v-data-table>
            </div>

            <!-- Pagination -->
            <div v-if="library.totalPage > 1" class="d-flex justify-end mt-3">
              <v-pagination
                v-model="library.page"
                :length="library.totalPage"
                :total-visible="$vuetify.breakpoint.xs ? 3 : 5"
                color="primary"
                circle
                @input="fetchLibrary"
              />
            </div>
          </v-col>

          <!-- Right: detail side panel -->
          <v-col v-if="detailPanel.show" cols="12" md="4" class="library-detail-col">
            <div class="detail-panel" v-if="detailPanel.item">
              <!-- Head -->
              <div class="detail-panel-head">
                <div class="detail-panel-head__icon">
                  <v-icon color="primary">mdi-bookshelf</v-icon>
                </div>
                <div class="flex-grow-1" style="min-width: 0;">
                  <div class="detail-panel-title">Library Content</div>
                  <div class="detail-panel-meta">
                    <v-icon size="13" color="grey">mdi-clock-outline</v-icon>
                    <span>{{ formatDate(detailPanel.item.createdAt) }}</span>
                  </div>
                </div>
                <v-btn icon small @click="closeDetail">
                  <v-icon>mdi-close</v-icon>
                </v-btn>
              </div>

              <v-divider />

              <!-- Body -->
              <div class="detail-panel-body">
                <!-- Attachments -->
                <div v-if="getMediaList(detailPanel.item).length" class="mb-4">
                  <div class="detail-section-label mb-2">
                    <v-icon size="16" color="primary" class="mr-1">mdi-paperclip</v-icon>
                    Attachments ({{ getMediaList(detailPanel.item).length }})
                  </div>
                  <div class="detail-attachments">
                    <a
                      v-for="(url, i) in getMediaList(detailPanel.item)"
                      :key="i"
                      :href="imageHelpers.url(url)"
                      target="_blank"
                      rel="noopener noreferrer"
                      class="detail-attachment-item"
                    >
                      <video v-if="isVideo(url)" :src="imageHelpers.url(url)" class="detail-attachment-media" muted />
                      <v-img
                        v-else
                        :src="imageHelpers.url(url, { width: 200, height: 200 })"
                        class="detail-attachment-media"
                        contain
                      />
                    </a>
                  </div>
                </div>

                <!-- Content -->
                <div>
                  <div class="detail-section-label mb-2 d-flex align-center justify-space-between">
                    <span>
                      <v-icon size="16" color="primary" class="mr-1">mdi-text-box-outline</v-icon>
                      Content
                    </span>
                    <v-btn
                      x-small
                      outlined
                      color="primary"
                      :class="{ 'detail-copy-btn--copied': isCopied(getContent(detailPanel.item)) }"
                      style="text-transform: none;"
                      @click="copyContent(getContent(detailPanel.item))"
                    >
                      <v-icon size="14" left>
                        {{ isCopied(getContent(detailPanel.item)) ? 'mdi-check' : 'mdi-content-copy' }}
                      </v-icon>
                      {{ isCopied(getContent(detailPanel.item)) ? 'Copied' : 'Copy' }}
                    </v-btn>
                  </div>
                  <div class="detail-content-box">
                    {{ getContent(detailPanel.item) || 'No content' }}
                  </div>
                </div>
              </div>
            </div>
          </v-col>
        </v-row>
      </v-card>
    </div>
  </div>
</template>

<script>
import { reportApi } from '@/apis/report';
import imageHelpers from '@/helpers/image';

export default {
  name: 'ContentLibraryPage',
  data() {
    return {
      imageHelpers,
      library: {
        search: '',
        page: 1,
        limit: 20,
        total: 0,
        totalPage: 1,
        isLoading: false,
        list: [],
      },
      searchDebounce: null,
      // Side detail panel state
      detailPanel: {
        show: false,
        item: null,
      },
      copiedText: '',
      copiedTimeout: null,
      tableHeaders: [
        { text: 'Attachments', value: 'resource_input', sortable: false, width: '18%' },
        { text: 'Content', value: 'content', sortable: false, width: '52%' },
        { text: 'Created At', value: 'createdAt', sortable: false, width: '16%' },
        { text: 'Actions', value: 'actions', sortable: false, width: '14%', align: 'end' },
      ],
    };
  },
  created() {
    this.fetchLibrary();
  },
  methods: {
    formatDate(val) {
      if (!val) return '—';
      const d = new Date(val);
      if (isNaN(d.getTime())) return '—';
      const pad = n => String(n).padStart(2, '0');
      return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`;
    },

    isVideo(url) {
      if (!url) return false;
      const ext = url
        .split('?')[0]
        .split('.')
        .pop()
        .toLowerCase();
      return ['mp4', 'webm', 'ogg', 'mov', 'avi'].includes(ext);
    },

    getMediaList(item) {
      if (!item) return [];
      if (Array.isArray(item.resource_input) && item.resource_input.length) return item.resource_input;
      if (Array.isArray(item.images) && item.images.length) return item.images;
      return [];
    },

    getContent(item) {
      if (!item) return '';
      return item.content || item.prompt || item.description || '';
    },

    async fetchLibrary() {
      this.library.isLoading = true;
      try {
        const { data } = await reportApi.getLibraryContentShared({
          search: this.library.search || '',
          page: String(this.library.page),
          limit: String(this.library.limit),
        });
        this.library.list = Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : [];
        const p = data?.pagination || {};
        this.library.total = p.total || 0;
        this.library.totalPage = p.totalPage || p.totalPages || 1;
      } catch (e) {
        console.error('fetchLibrary', e);
        this.library.list = [];
        this.library.total = 0;
        this.library.totalPage = 1;
      } finally {
        this.library.isLoading = false;
      }
    },

    onLibrarySearch() {
      if (this.searchDebounce) clearTimeout(this.searchDebounce);
      this.searchDebounce = setTimeout(() => {
        this.library.page = 1;
        this.fetchLibrary();
      }, 350);
    },

    openDetail(item) {
      // Toggle off if clicking the row that's already open
      if (this.detailPanel.show && this.detailPanel.item && this.detailPanel.item._id === item._id) {
        this.closeDetail();
        return;
      }
      this.detailPanel.item = item;
      this.detailPanel.show = true;
    },

    closeDetail() {
      this.detailPanel.show = false;
      this.detailPanel.item = null;
    },

    isActiveDetail(item) {
      return this.detailPanel.show && this.detailPanel.item && item && this.detailPanel.item._id === item._id;
    },

    async copyContent(text) {
      const value = (text || '').toString();
      if (!value) return;
      try {
        if (navigator.clipboard && window.isSecureContext) {
          await navigator.clipboard.writeText(value);
        } else {
          const ta = document.createElement('textarea');
          ta.value = value;
          ta.style.position = 'fixed';
          ta.style.opacity = '0';
          document.body.appendChild(ta);
          ta.focus();
          ta.select();
          document.execCommand('copy');
          document.body.removeChild(ta);
        }
        this.copiedText = value;
        if (this.copiedTimeout) clearTimeout(this.copiedTimeout);
        this.copiedTimeout = setTimeout(() => {
          this.copiedText = '';
        }, 1600);
      } catch (err) {
        console.error('copyContent', err);
        this.$store?.commit?.('setMessages', {
          messages: 'Failed to copy content',
          type: 'error',
        });
      }
    },

    isCopied(text) {
      return !!text && this.copiedText === text;
    },
  },
};
</script>

<style lang="scss" scoped>
.library-page {
  min-height: 100vh;
  background: #f4f6fb;
  display: flex;
  flex-direction: column;
}

.library-header {
  background: #ffffff;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
  position: sticky;
  top: 0;
  z-index: 10;
}

.library-body {
  flex: 1;
  align-items: flex-start;
}

.library-search {
  max-width: 280px;

  ::v-deep .v-input__slot {
    min-height: 36px !important;
  }

  ::v-deep .v-input__prepend-inner {
    margin-top: 8px !important;
  }
}

/* ===== Table column ===== */
.library-row {
  transition: gap 0.2s;
}

.library-table-col {
  transition: max-width 0.2s, flex 0.2s;
}

/* ===== Side detail panel ===== */
.library-detail-col {
  border-left: 1px solid #ececf3;
}

.detail-panel {
  background: #fafbfc;
  border-radius: 10px;
  border: 1px solid #ececf3;
  display: flex;
  flex-direction: column;
  max-height: calc(100vh - 220px);
  position: sticky;
  top: 80px;
  overflow: hidden;
}

.detail-panel-head {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  background: linear-gradient(135deg, #f0f4ff 0%, #ffffff 100%);
  flex-shrink: 0;
}

.detail-panel-head__icon {
  width: 34px;
  height: 34px;
  border-radius: 8px;
  background: #e8eaff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.detail-panel-title {
  font-size: 14px;
  font-weight: 600;
  color: #1a1a1a;
  line-height: 1.3;
}

.detail-panel-meta {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 11.5px;
  color: #6b7280;
  margin-top: 2px;
}

.detail-panel-body {
  padding: 14px;
  overflow-y: auto;
  flex: 1;
}

.detail-section-label {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #5c6bc0;
  display: flex;
  align-items: center;
}

.detail-attachments {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(86px, 1fr));
  gap: 6px;
}

.detail-attachment-item {
  display: block;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  overflow: hidden;
  background: #fafafa;
  aspect-ratio: 1;
  transition: transform 0.15s, box-shadow 0.15s;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }
}

.detail-attachment-media {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.detail-content-box {
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 12px 14px;
  font-size: 13px;
  line-height: 1.55;
  color: #1f2937;
  white-space: pre-wrap;
  word-break: break-word;
  max-height: 360px;
  overflow-y: auto;
}

.detail-copy-btn--copied {
  background: rgba(76, 175, 80, 0.12);
  border-color: #4caf50 !important;
  color: #2e7d32 !important;
}

/* ===== Table ===== */
.table-scroll-wrapper {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  border-radius: 8px;
}

.report-table {
  border-radius: 8px;

  ::v-deep thead th {
    background: #f0f4ff !important;
    font-size: 12px !important;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: #3f51b5 !important;
    padding-top: 14px !important;
    padding-bottom: 14px !important;
  }

  ::v-deep tbody td {
    padding-top: 14px !important;
    padding-bottom: 14px !important;
    font-size: 14px !important;
  }

  ::v-deep tbody tr:hover {
    background: #f8f9ff !important;
  }
}

.library-table {
  ::v-deep .v-data-table__wrapper {
    overflow-x: hidden;
  }

  ::v-deep table {
    table-layout: fixed;
    width: 100%;
  }

  ::v-deep th,
  ::v-deep td {
    padding-left: 8px !important;
    padding-right: 8px !important;
  }
}

.cell-clamp {
  max-width: 100%;
  font-size: 13px;
  line-height: 1.45;
  white-space: pre-wrap;
  word-break: break-word;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  cursor: default;
}

.created-cell {
  font-size: 12.5px;
  white-space: nowrap;
}

.attachment-thumb {
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  transition: transform 0.1s, box-shadow 0.1s;

  &:hover {
    transform: scale(1.08);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.18);
  }
}

.content-cell {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding-right: 8px;

  .cell-clamp {
    flex: 1 1 auto;
    min-width: 0;
  }

  .copy-inline-btn {
    flex: 0 0 auto;
    margin-right: 2px;
    opacity: 0.55;
    transition: opacity 0.15s, background 0.15s;

    &:hover {
      opacity: 1;
      background: rgba(63, 81, 181, 0.08);
    }
  }
}

.row-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 6px;
  flex-wrap: wrap;
}

.row-action-btn {
  text-transform: none !important;
  letter-spacing: 0 !important;
  height: 30px !important;
  min-width: 84px !important;
  padding: 0 10px !important;
  border-radius: 6px !important;
  font-size: 12.5px !important;
  font-weight: 500 !important;
  background: #f5f7ff !important;

  &--active {
    background: var(--v-primary-base, #3f51b5) !important;
    color: #ffffff !important;
  }
}

/* ===== Responsive ===== */
@media (max-width: 959px) {
  /* On mobile/tablet, the side panel stacks below the table (full width) */
  .library-detail-col {
    border-left: none;
    border-top: 1px solid #ececf3;
    margin-top: 16px;
    padding-top: 16px;
  }

  .detail-panel {
    position: relative;
    top: 0;
    max-height: none;
  }
}

@media (max-width: 599px) {
  .library-header h2 {
    font-size: 16px !important;
  }

  .library-search {
    max-width: 100%;
    flex: 1 1 100%;
  }

  /* Allow horizontal scroll for the 4-column table on small screens */
  .library-table {
    ::v-deep .v-data-table__wrapper {
      overflow-x: auto !important;
    }

    ::v-deep table {
      table-layout: auto !important;
      min-width: 600px;
    }
  }
}
</style>
